import React, { useState } from "react";
import { appointmentRepo, customerRepo, productRepo, resourceRepo, staffRepo } from "../../services";
import { nextRowCode } from "../../services/commerce";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, DataTable, Input, Modal, PageHead, Select, baht, formatDate, useAsync, useTableState } from "../../components/ui";

const STATUS = ["pending", "confirmed", "completed", "cancelled", "noshow"];
const EMPTY = { customerId: "", productId: "", staffId: "", resourceId: "", startDate: "", startTime: "10:00" };

export default function Appointments() {
  const { orgId, branchId, toast } = useApp();
  const { tx } = useI18n();
  const tableState = useTableState();
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data, loading, reload } = useAsync(
    () =>
      appointmentRepo.list(orgId, {
        page: tableState.page,
        perPage: tableState.perPage,
        search: tableState.search,
        sortBy: "startAt",
        sortDir: "desc",
        filter: (row) => (!branchId || row.branchId === branchId) && (!status || row.status === status),
      }),
    [orgId, branchId, status, tableState.page, tableState.perPage, tableState.search]
  );

  const { data: customers } = useAsync(() => customerRepo.all(orgId, (row) => !branchId || row.branchId === branchId), [orgId, branchId], []);
  const { data: products } = useAsync(() => productRepo.all(orgId), [orgId], []);
  const { data: staff } = useAsync(() => staffRepo.all(orgId), [orgId], []);
  const { data: resources } = useAsync(() => resourceRepo.all(orgId), [orgId], []);

  const updateStatus = async (row, next) => {
    await appointmentRepo.update(orgId, row.id, { status: next });
    reload();
  };

  const save = async () => {
    const customer = customers?.find((c) => c.id === form.customerId);
    const product = products?.find((p) => p.id === form.productId);
    if (!customer || !product || !form.startDate) return toast(tx("กรอกข้อมูลให้ครบ", "Please fill all fields"), "error");

    await appointmentRepo.create(orgId, {
      code: await nextRowCode(appointmentRepo, orgId, { prefix: "AP", docType: "appointment" }),
      customerId: customer.id,
      customerName: customer.name,
      customerPhone: customer.phone,
      productId: product.id,
      serviceName: product.name,
      staffId: form.staffId,
      resourceId: form.resourceId,
      branchId: customer.branchId,
      startAt: new Date(`${form.startDate}T${form.startTime}`).toISOString(),
      durationMin: product.duration || 60,
      status: "confirmed",
      price: product.price,
      depositAmount: 0,
      depositPaid: false,
    });
    setOpen(false);
    setForm(EMPTY);
    reload();
    toast(tx("สร้างนัดหมายเรียบร้อย", "Appointment created"), "success");
  };

  return (
    <>
      <PageHead
        title={tx("รายการนัดหมาย", "Appointments")}
        actions={<Button variant="primary" onClick={() => setOpen(true)}>+ {tx("สร้างนัดหมาย", "New appointment")}</Button>}
      />

      <div className="filter-bar">
        <input className="input-crm" style={{ maxWidth: 260 }} placeholder={tx("ค้นหา", "Search")} value={tableState.search} onChange={(e) => tableState.setSearch(e.target.value)} />
        <Select options={STATUS} value={status} onChange={(e) => setStatus(e.target.value)} placeholder={tx("ทุกสถานะ", "All status")} style={{ width: "auto" }} />
      </div>

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        emptyText={tx("ยังไม่มีนัดหมาย", "No appointments")}
        columns={[
          { key: "code", label: tx("รหัส", "Code") },
          { key: "customerName", label: tx("ลูกค้า", "Customer") },
          { key: "serviceName", label: tx("บริการ", "Service") },
          { key: "startAt", label: tx("วัน-เวลา", "Date"), render: (row) => formatDate(row.startAt, true) },
          { key: "price", label: tx("ราคา", "Price"), align: "right", render: (row) => baht(row.price) },
          {
            key: "status",
            label: tx("สถานะ", "Status"),
            render: (row) => (
              <select className="select-crm" style={{ width: "auto", padding: "3px 8px" }} value={row.status} onChange={(e) => updateStatus(row, e.target.value)}>
                {STATUS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ),
          },
        ]}
      />

      <Modal
        open={open}
        title={tx("สร้างนัดหมายใหม่", "New appointment")}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{tx("ยกเลิก", "Cancel")}</Button>
            <Button variant="primary" onClick={save}>{tx("บันทึก", "Save")}</Button>
          </>
        }
      >
        <Select label={tx("ลูกค้า", "Customer")} placeholder={tx("เลือกลูกค้า", "Select customer")} options={(customers || []).map((c) => ({ value: c.id, label: c.name }))} value={form.customerId} onChange={(e) => setForm({ ...form, customerId: e.target.value })} />
        <Select label={tx("บริการ", "Service")} placeholder={tx("เลือกบริการ", "Select service")} options={(products || []).map((p) => ({ value: p.id, label: `${p.name} (฿${p.price})` }))} value={form.productId} onChange={(e) => setForm({ ...form, productId: e.target.value })} />
        <Select label={tx("ผู้ให้บริการ", "Staff")} placeholder={tx("เลือกผู้ให้บริการ", "Select staff")} options={(staff || []).map((s) => ({ value: s.id, label: s.name }))} value={form.staffId} onChange={(e) => setForm({ ...form, staffId: e.target.value })} />
        <Select label={tx("ทรัพยากร", "Resource")} placeholder={tx("เลือกห้อง/ทรัพยากร", "Select resource")} options={(resources || []).map((r) => ({ value: r.id, label: r.name }))} value={form.resourceId} onChange={(e) => setForm({ ...form, resourceId: e.target.value })} />
        <div className="grid-2">
          <Input label={tx("วันที่", "Date")} type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          <Input label={tx("เวลา", "Time")} type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
        </div>
      </Modal>
    </>
  );
}
